// src/components/TaskBoard.tsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, Plus, Trash2, Briefcase, TrendingUp, Percent, RussianRuble, RefreshCw, Edit3, Check } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';

interface Task {
  id: string;
  title: string;
  completed: boolean;
  createdAt: number;
  order?: number;
}

interface Project {
  id: string;
  title: string;
  tasks: Task[];
}

interface TaskBoardProps {
  isOpen: boolean;
  onToggle: () => void;
}

// Виджет метрик для свёрнутого режима
const MetricsWidget: React.FC = () => {
  const [metrics, setMetrics] = useState({
    openVacancies: 0,
    activeRequisitions: 0,
    conversionRate: 0,
    openFee: 0,
    closedFee: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const [vacRes, reqRes, candRes, stagesRes, feeRes] = await Promise.all([
          api.get('/vacancies'),
          api.get('/requisitions'),
          api.get('/candidates'),
          api.get('/candidate-stages'),
          api.get('/vacancies/fee-stats').catch(() => ({ data: { openFee: 0, closedFee: 0 } })),
        ]);

        const openVacancies = vacRes.data.filter(
          (v: any) => v.status !== 'closed_by_client' && v.status !== 'cancelled'
        ).length;
        const activeRequisitions = reqRes.data.filter(
          (r: any) => r.status === 'approved' || r.status === 'pending'
        ).length;

        const candidates = candRes.data;
        const stages = stagesRes.data;
        const hiredStage = stages.find((s: any) => s.title === 'Наняты');
        const hiredStageId = hiredStage?._id;
        const hired = hiredStageId
          ? candidates.filter((c: any) => c.stage === hiredStageId).length
          : 0;
        const conversionRate = candidates.length > 0 ? (hired / candidates.length) * 100 : 0;

        setMetrics({
          openVacancies,
          activeRequisitions,
          conversionRate,
          openFee: feeRes.data.openFee || 0,
          closedFee: feeRes.data.closedFee || 0,
        });
      } catch (err) {
        console.error('Ошибка загрузки метрик:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  if (loading) {
    return (
      <div style={metricsStyles.loading}>
        <div style={metricsStyles.spinner} />
      </div>
    );
  }

  return (
    <div style={metricsStyles.container}>
      <div style={metricsStyles.item}>
        <Briefcase size={14} color="#2575fc" />
        <span style={metricsStyles.value}>{metrics.openVacancies}</span>
        <span style={metricsStyles.label}>вакансий</span>
      </div>
      <div style={metricsStyles.item}>
        <TrendingUp size={14} color="#2575fc" />
        <span style={metricsStyles.value}>{metrics.activeRequisitions}</span>
        <span style={metricsStyles.label}>заявок</span>
      </div>
      <div style={metricsStyles.item}>
        <Percent size={14} color="#2575fc" />
        <span style={metricsStyles.value}>{metrics.conversionRate.toFixed(1)}%</span>
        <span style={metricsStyles.label}>конверсия</span>
      </div>
      <div style={metricsStyles.item}>
        <RussianRuble size={14} color="#28a745" />
        <span style={metricsStyles.value}>{metrics.openFee.toLocaleString()} </span>
        <span style={metricsStyles.label}>гонорар откр.</span>
      </div>
      <div style={metricsStyles.item}>
        <RussianRuble size={14} color="#ff4d4f" />
        <span style={metricsStyles.value}>{metrics.closedFee.toLocaleString()} </span>
        <span style={metricsStyles.label}>гонорар закр.</span>
      </div>
    </div>
  );
};

const metricsStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '4px',
    width: '100%',
    boxSizing: 'border-box',
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
    padding: '6px 4px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '10px',
    backdropFilter: 'blur(5px)',
  },
  value: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#1a1a2e',
    lineHeight: 1.2,
  },
  label: {
    fontSize: '10px',
    color: '#555',
    textAlign: 'center',
    lineHeight: 1.2,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
  },
  spinner: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid rgba(37,117,252,0.2)',
    borderTopColor: '#2575fc',
    animation: 'spin 0.8s linear infinite',
  },
};

const TaskBoard: React.FC<TaskBoardProps> = React.memo(({ isOpen, onToggle }) => {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [newTaskTitles, setNewTaskTitles] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Редактирование проектов
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [projectEditTitle, setProjectEditTitle] = useState('');
  const projectInputRef = useRef<HTMLInputElement>(null);

  // Редактирование задач
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [taskEditTitle, setTaskEditTitle] = useState('');
  const taskInputRef = useRef<HTMLInputElement>(null);

  const fetchProjects = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/projects');
      const projectsData = res.data.map((p: any) => ({
        id: String(p._id || p.id),
        title: p.title,
        tasks: (p.tasks || [])
          .map((t: any) => ({
            id: String(t._id || t.id),
            title: t.title,
            completed: t.completed,
            createdAt: t.createdAt,
            order: t.order ?? 0,
          }))
          .sort((a: Task, b: Task) => (a.order ?? 0) - (b.order ?? 0)),
      }));
      setProjects(projectsData);
    } catch (err: any) {
      console.error('❌ Ошибка загрузки проектов:', err.response?.data || err.message);
      setError('Не удалось загрузить проекты');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [user]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchProjects();
  };

  const addProject = async () => {
    if (!newProjectTitle.trim()) return;
    try {
      const res = await api.post('/projects', { title: newProjectTitle });
      const newProject = {
        id: res.data.id || res.data._id,
        title: res.data.title,
        tasks: [],
      };
      setProjects(prev => [...prev, newProject]);
      setNewProjectTitle('');
    } catch (err: any) {
      console.error('❌ Ошибка при создании проекта:', err.response?.data || err.message);
      alert('Ошибка при создании проекта');
    }
  };

  const deleteProject = async (projectId: string) => {
    try {
      await api.delete(`/projects/${projectId}`);
      setProjects(prev => prev.filter(p => p.id !== projectId));
    } catch (err: any) {
      console.error('❌ Ошибка при удалении проекта:', err.response?.data || err.message);
      alert('Ошибка при удалении проекта');
    }
  };

  // Начать редактирование названия проекта
  const startEditProject = (project: Project) => {
    setEditingProjectId(project.id);
    setProjectEditTitle(project.title);
    setTimeout(() => projectInputRef.current?.focus(), 0);
  };

  const saveProjectTitle = async () => {
    if (!editingProjectId || !projectEditTitle.trim()) return;
    try {
      await api.put(`/projects/${editingProjectId}`, { title: projectEditTitle });
      setProjects(prev =>
        prev.map(p => (p.id === editingProjectId ? { ...p, title: projectEditTitle } : p))
      );
      setEditingProjectId(null);
    } catch (err) {
      console.error('Ошибка сохранения названия проекта:', err);
      alert('Не удалось сохранить название');
    }
  };

  const cancelEditProject = () => {
    setEditingProjectId(null);
  };

  // Редактирование задач
  const startEditTask = (task: Task) => {
    setEditingTaskId(task.id);
    setTaskEditTitle(task.title);
    setTimeout(() => taskInputRef.current?.focus(), 0);
  };

  const saveTaskTitle = async () => {
    if (!editingTaskId || !taskEditTitle.trim()) return;
    try {
      await api.put(`/tasks/${editingTaskId}`, { title: taskEditTitle });
      setProjects(prev =>
        prev.map(p => ({
          ...p,
          tasks: p.tasks.map(t =>
            t.id === editingTaskId ? { ...t, title: taskEditTitle } : t
          ),
        }))
      );
      setEditingTaskId(null);
    } catch (err) {
      console.error('Ошибка сохранения названия задачи:', err);
      alert('Не удалось сохранить название задачи');
    }
  };

  const cancelEditTask = () => {
    setEditingTaskId(null);
  };

  const addTask = async (projectId: string) => {
    const title = newTaskTitles[projectId]?.trim();
    if (!title) return;
    try {
      const res = await api.post(`/projects/${projectId}/tasks`, { title });
      const newTask: Task = {
        id: res.data.id || res.data._id,
        title: res.data.title,
        completed: res.data.completed || false,
        createdAt: res.data.createdAt || Date.now(),
        order: res.data.order ?? projects.find(p => p.id === projectId)?.tasks.length ?? 0,
      };
      setProjects(prev =>
        prev.map(p =>
          p.id === projectId ? { ...p, tasks: [...p.tasks, newTask] } : p
        )
      );
      setNewTaskTitles({ ...newTaskTitles, [projectId]: '' });
    } catch (err: any) {
      console.error('❌ Ошибка при добавлении задачи:', err.response?.data || err.message);
      alert(`Ошибка при добавлении задачи: ${err.response?.data?.error || err.message}`);
    }
  };

  const toggleTask = async (projectId: string, taskId: string) => {
    const project = projects.find(p => p.id === projectId);
    const task = project?.tasks.find(t => t.id === taskId);
    if (!task) return;
    const oldCompleted = task.completed;
    setProjects(prev =>
      prev.map(p =>
        p.id === projectId
          ? {
              ...p,
              tasks: p.tasks.map(t =>
                t.id === taskId ? { ...t, completed: !t.completed } : t
              ),
            }
          : p
      )
    );
    try {
      await api.put(`/tasks/${taskId}`, { completed: !oldCompleted });
    } catch (err: any) {
      console.error('❌ Ошибка при переключении задачи:', err.response?.data || err.message);
      setProjects(prev =>
        prev.map(p =>
          p.id === projectId
            ? {
                ...p,
                tasks: p.tasks.map(t =>
                  t.id === taskId ? { ...t, completed: oldCompleted } : t
                ),
              }
            : p
        )
      );
      alert('Ошибка при обновлении задачи');
    }
  };

  const deleteTask = async (projectId: string, taskId: string) => {
    const project = projects.find(p => p.id === projectId);
    const taskToDelete = project?.tasks.find(t => t.id === taskId);
    if (!taskToDelete) return;
    setProjects(prev =>
      prev.map(p =>
        p.id === projectId
          ? { ...p, tasks: p.tasks.filter(t => t.id !== taskId) }
          : p
      )
    );
    try {
      await api.delete(`/tasks/${taskId}`);
    } catch (err: any) {
      console.error('❌ Ошибка при удалении задачи:', err.response?.data || err.message);
      setProjects(prev =>
        prev.map(p =>
          p.id === projectId
            ? { ...p, tasks: [...p.tasks, taskToDelete] }
            : p
        )
      );
      alert('Ошибка при удалении задачи');
    }
  };

  const onDragEnd = useCallback(
    async (result: DropResult, projectId: string) => {
      const { destination, source } = result;
      if (!destination) return;
      if (destination.droppableId === source.droppableId && destination.index === source.index) return;

      const project = projects.find(p => p.id === projectId);
      if (!project) return;

      const reorderedTasks = Array.from(project.tasks);
      const [removed] = reorderedTasks.splice(source.index, 1);
      reorderedTasks.splice(destination.index, 0, removed);

      setProjects(prev =>
        prev.map(p =>
          p.id === projectId ? { ...p, tasks: reorderedTasks } : p
        )
      );

      try {
        await api.put(`/projects/${projectId}/tasks/reorder`, {
          taskIds: reorderedTasks.map(t => t.id),
        });
      } catch (err) {
        console.error('❌ Ошибка сохранения порядка задач:', err);
        setProjects(prev =>
          prev.map(p =>
            p.id === projectId ? { ...p, tasks: project.tasks } : p
          )
        );
        alert('Не удалось сохранить порядок задач');
      }
    },
    [projects]
  );

  const processedProjects = useMemo(() => {
    return projects.map(project => {
      const incompleteTasks = project.tasks.filter(t => !t.completed);
      const completedTasks = project.tasks.filter(t => t.completed);
      return {
        ...project,
        allTasks: [...incompleteTasks, ...completedTasks],
      };
    });
  }, [projects]);

  // ★ DnD: рендер клона проекта вне containing block
  const renderProjectClone = (provided: any, snapshot: any, rubric: any) => {
    const projectId = rubric.draggableId;
    const proj = processedProjects.find((p: any) => p.id === projectId);
    if (!proj) return null;
    const done = (proj.allTasks || []).filter((t: any) => t.completed).length;
    const total = (proj.allTasks || []).length;
    const progress = total > 0 ? (done / total) * 100 : 0;
    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        style={{
          ...provided.draggableProps.style,
          background: 'rgba(255,255,255,0.98)',
          borderRadius: 18,
          padding: '14px 16px',
          border: 'none',
          boxShadow: '0 20px 48px rgba(31,38,135,0.25), inset 0 0 0 1px rgba(255,255,255,0.9)',
          width: 288,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ width: 4, height: 20, borderRadius: 2, background: 'linear-gradient(180deg, #6366f1, #8b5cf6)', flexShrink: 0 }} />
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', margin: 0, flex: 1 }}>{proj.title}</h4>
          <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{done}/{total}</span>
        </div>
        <div style={{ height: 3, background: 'rgba(99,102,241,0.15)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${progress}%`, height: '100%', background: 'linear-gradient(90deg, #6366f1, #8b5cf6)' }} />
        </div>
      </div>
    );
  };

  // ★ DnD: рендер клона задачи вне containing block
  const renderTaskClone = (provided: any, snapshot: any, rubric: any) => {
    const taskId = rubric.draggableId;
    let foundTask: any = null;
    for (const proj of processedProjects) {
      const t = proj.allTasks?.find((x: any) => x.id === taskId);
      if (t) { foundTask = t; break; }
    }
    if (!foundTask) return null;
    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        style={{
          ...provided.draggableProps.style,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '7px 12px',
          background: 'rgba(255,255,255,0.98)',
          borderRadius: 12,
          boxShadow: '0 12px 32px rgba(31,38,135,0.2)',
          cursor: 'grabbing',
        }}
      >
        <div
          className={`tb-checkbox-custom ${foundTask.completed ? 'checked' : ''}`}
          style={{
            width: 18, height: 18, borderRadius: 6,
            border: foundTask.completed ? 'none' : '2px solid #cbd5e1',
            background: foundTask.completed ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'transparent',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {foundTask.completed && <Check size={12} color="#fff" strokeWidth={3} />}
        </div>
        <span style={{ fontSize: 13, color: foundTask.completed ? '#94a3b8' : '#1a1a2e', textDecoration: foundTask.completed ? 'line-through' : 'none' }}>
          {foundTask.title}
        </span>
      </div>
    );
  };

  // ★ DnD: глобальный обработчик (проекты + задачи)
  const handleGlobalDragEnd = async (result: DropResult) => {
    const { source, destination, type } = result;
    if (!destination) return;

    if (type === 'PROJECT') {
      const items: any[] = Array.from(processedProjects as any[]);
      const [moved] = items.splice(source.index, 1);
      items.splice(destination.index, 0, moved);
      const newOrder = items.map((p: any) => String(p.id));
      setProjects((prev: any[]) => {
        const map = new Map(prev.map((p: any) => [p.id, p]));
        return newOrder.map((id: string) => map.get(id)).filter(Boolean) as any;
      });
      try {
        await api.put('/projects/reorder', { ids: newOrder });
      } catch (e) {
        console.error('reorder projects failed', e);
        fetchProjects();
      }
    } else if (type === 'TASK') {
      const projectId = source.droppableId.replace('tasks-', '');
      onDragEnd(result, projectId);
    }
  };

  // Глобальный обработчик клика для снятия фокуса редактирования
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (projectInputRef.current && !projectInputRef.current.contains(e.target as Node)) {
        if (editingProjectId) saveProjectTitle();
      }
      if (taskInputRef.current && !taskInputRef.current.contains(e.target as Node)) {
        if (editingTaskId) saveTaskTitle();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editingProjectId, projectEditTitle, editingTaskId, taskEditTitle]);

  if (!isOpen) {
    return (
      <div style={styles.closedPanel}>
        <div style={styles.closedLogo}>
          <img src="/images/лама.png" alt="Лого" style={styles.logoIcon} />
        </div>
        <button
          onClick={onToggle}
          style={styles.toggleButtonClosed}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, #6a11cb, #2575fc)';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
            e.currentTarget.style.color = '#2575fc';
          }}
          title="Развернуть"
        >
          <ChevronRight size={18} />
        </button>
        <MetricsWidget />
      </div>
    );
  }

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <div style={styles.logo}>
          <img src="/images/лама.png" alt="Лого" style={styles.logoIcon} />
          <span style={styles.logoText}>Задачник</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleRefresh}
            style={styles.refreshButton}
            title="Обновить"
            disabled={refreshing}
          >
            <RefreshCw size={16} style={refreshing ? { animation: 'spin 1s linear infinite' } : {}} />
          </button>
          <button
            onClick={onToggle}
            style={styles.toggleButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #6a11cb, #2575fc)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.color = '#2575fc';
            }}
            title="Свернуть"
          >
            <ChevronLeft size={18} />
          </button>
        </div>
      </div>

      <div style={styles.addProject}>
        <input
          type="text"
          placeholder="Название проекта"
          value={newProjectTitle}
          onChange={(e) => setNewProjectTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') addProject();
          }}
          style={styles.input}
        />
        <button onClick={addProject} style={styles.addButton} title="Создать проект">
          <Plus size={18} />
        </button>
      </div>

      {loading && !refreshing ? (
        <p>Загрузка...</p>
      ) : error ? (
        <p style={{ color: 'red' }}>{error}</p>
      ) : (
        <DragDropContext onDragEnd={handleGlobalDragEnd}>
        <Droppable droppableId="projects" type="PROJECT" renderClone={renderProjectClone}>
          {(providedProjects) => (
        <div ref={providedProjects.innerRef} {...providedProjects.droppableProps} style={styles.projectsList}>
          {processedProjects.map((project, index) => (
            <Draggable key={project.id} draggableId={project.id} index={index}>
              {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              style={{
                ...provided.draggableProps.style,
                ...styles.projectCard,
                ...(snapshot.isDragging && { boxShadow: '0 16px 48px rgba(31,38,135,0.25)' }),
              }}
              className="tb-project-card"
            >
              <div style={styles.projectHeader} {...provided.dragHandleProps}>
                <div style={{ width: 4, height: 20, borderRadius: 2, background: 'linear-gradient(180deg, #6366f1, #8b5cf6)', flexShrink: 0 }} />
                {editingProjectId === project.id ? (
                  <input
                    ref={projectInputRef}
                    type="text"
                    value={projectEditTitle}
                    onChange={e => setProjectEditTitle(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') saveProjectTitle();
                      if (e.key === 'Escape') cancelEditProject();
                    }}
                    onBlur={saveProjectTitle}
                    style={{ ...styles.projectTitleInput, flex: 1, marginRight: '8px' }}
                  />
                ) : (
                  <h4
                    style={styles.projectTitle}
                    onDoubleClick={() => startEditProject(project)}
                    title="Двойной клик для редактирования"
                  >
                    {project.title}
                  </h4>
                )}
                <span style={{ fontSize: 11, color: '#64748b', fontVariant: 'tabular-nums', fontWeight: 600, flexShrink: 0 }}>
                  {(project.allTasks || []).filter((t: any) => t.completed).length}/{(project.allTasks || []).length}
                </span>
                <button
                  onClick={() => deleteProject(project.id)}
                  style={styles.deleteProjectBtn}
                  className="tb-delete-btn"
                  title="Удалить проект"
                >
                  <Trash2 size={14} color="#ff4d4f" />
                </button>
              </div>
              <div style={{ height: 3, background: 'rgba(99,102,241,0.15)', borderRadius: 2, marginBottom: 10, overflow: 'hidden' }}>
                <div style={{
                  width: `${(() => {
                    const total = (project.allTasks || []).length;
                    const done = (project.allTasks || []).filter((t: any) => t.completed).length;
                    return total > 0 ? (done / total) * 100 : 0;
                  })()}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                  transition: 'width 0.4s cubic-bezier(0.4,0,0.2,1)',
                }} />
              </div>

              <Droppable droppableId={`tasks-${project.id}`} type="TASK" renderClone={renderTaskClone}>
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={styles.tasksList}
                    >
                      {project.allTasks.map((task, index) => (
                        <Draggable key={task.id} draggableId={task.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className="tb-task-row"
                              style={{
                                ...styles.taskItem,
                                opacity: snapshot.isDragging ? 0.8 : task.completed ? 0.6 : 1,
                                ...provided.draggableProps.style,
                                ...(snapshot.isDragging && {
                                  backgroundColor: 'rgba(255,255,255,0.95)',
                                  boxShadow: '0 12px 32px rgba(31,38,135,0.15)',
                                  borderRadius: '12px',
                                  opacity: 0.95,
                                }),
                              }}
                            >
                              <label style={styles.taskLabel}>
                                <input
                                  type="checkbox"
                                  checked={task.completed}
                                  onChange={() => toggleTask(project.id, task.id)}
                                  style={{ display: 'none' }}
                                />
                                <div
                                  className={`tb-checkbox-custom ${task.completed ? 'checked' : ''}`}
                                  style={{
                                    width: 18, height: 18, borderRadius: 6,
                                    border: task.completed ? 'none' : '2px solid #cbd5e1',
                                    background: task.completed ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'transparent',
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  {task.completed && <Check size={12} color="#fff" strokeWidth={3} />}
                                </div>
                                {editingTaskId === task.id ? (
                                  <input
                                    ref={taskInputRef}
                                    type="text"
                                    value={taskEditTitle}
                                    onChange={e => setTaskEditTitle(e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') saveTaskTitle();
                                      if (e.key === 'Escape') cancelEditTask();
                                    }}
                                    onBlur={saveTaskTitle}
                                    style={styles.editTaskInput}
                                  />
                                ) : (
                                  <span
                                    onDoubleClick={() => startEditTask(task)}
                                    style={
                                      task.completed
                                        ? { ...styles.taskTitle, textDecoration: 'line-through', color: '#999' }
                                        : styles.taskTitle
                                    }
                                    title="Двойной клик для редактирования"
                                  >
                                    {task.title}
                                  </span>
                                )}
                              </label>
                              <button
                                onClick={() => deleteTask(project.id, task.id)}
                                style={styles.deleteTaskBtn}
                                className="tb-delete-btn"
                              >
                                <Trash2 size={12} color="#999" />
                              </button>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>

              <div style={styles.addTask}>
                <input
                  type="text"
                  placeholder="Новая задача"
                  value={newTaskTitles[project.id] || ''}
                  onChange={(e) =>
                    setNewTaskTitles({ ...newTaskTitles, [project.id]: e.target.value })
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addTask(project.id);
                  }}
                  style={styles.taskInput}
                />
                <button onClick={() => addTask(project.id)} style={styles.addTaskButton}>
                  <Plus size={14} />
                </button>
              </div>
            </div>
              )}
            </Draggable>
          ))}
          {providedProjects.placeholder}
        </div>
          )}
        </Droppable>
        </DragDropContext>
      )}
    </div>
  );
});

const styles: Record<string, React.CSSProperties> = {
  panel: {
    width: '320px',
    minWidth: '280px',
    background: 'rgba(235, 228, 255, 0.88)',
    borderRadius: '20px',
    padding: '16px',
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.2), 0 20px 40px rgba(0,0,0,0.25), 0 8px 20px rgba(0,0,0,0.15)',
    height: 'fit-content',
    maxHeight: 'calc(100vh - 100px)',
    overflowY: 'hidden',
    marginRight: '20px',
    transition: 'width 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
  },
  closedPanel: {
    width: '80px',
    background: 'rgba(106, 17, 203, 0.2)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    borderRadius: '20px',
    padding: '16px 4px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.25), 0 8px 20px rgba(0,0,0,0.15)',
    border: '1px solid rgba(255,255,255,0.2)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginRight: '20px',
    transition: 'width 0.3s ease',
  },
  closedLogo: { marginBottom: '8px' },
  logoIcon: { height: '32px', width: 'auto' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    flexShrink: 0,
  },
  logo: { display: 'flex', alignItems: 'center', gap: '8px' },
  logoText: { fontSize: '16px', fontWeight: 700, color: '#1a1a2e' },
  refreshButton: {
    width: '28px',
    height: '28px',
    borderRadius: '14px',
    background: 'white',
    border: '1px solid #2575fc',
    color: '#2575fc',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    transition: 'all 0.2s ease',
  },
  toggleButton: {
    width: '28px',
    height: '28px',
    borderRadius: '14px',
    background: 'white',
    border: '1px solid #2575fc',
    color: '#2575fc',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    transition: 'all 0.2s ease',
  },
  toggleButtonClosed: {
    width: '28px',
    height: '28px',
    borderRadius: '14px',
    background: 'white',
    border: '1px solid #2575fc',
    color: '#2575fc',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    marginBottom: '12px',
    transition: 'all 0.2s ease',
  },
  addProject: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    flexShrink: 0,
  },
  input: {
    flex: 1,
    padding: '9px 14px',
    borderRadius: '12px',
    border: '1px solid rgba(99,102,241,0.2)',
    background: 'rgba(255,255,255,0.85)',
    fontSize: '13px',
    outline: 'none',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
  },
  addButton: {
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    width: '38px',
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
    flexShrink: 0,
  },
  projectsList: { display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, overflowY: 'auto', minHeight: 0, padding: '4px 8px 16px 4px', overflowAnchor: 'none' },
  projectCard: {
    background: 'rgba(255,255,255,0.92)',
    borderRadius: '18px',
    padding: '14px 16px',
    border: 'none',
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.85), 0 8px 24px rgba(31,38,135,0.08)',
  },
  projectHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: '10px' },
  projectTitle: { fontSize: '14px', fontWeight: 700, color: '#1a1a2e', margin: 0, cursor: 'pointer', flex: 1 },
  projectTitleInput: {
    padding: '4px 8px',
    borderRadius: '8px',
    border: '1px solid #2575fc',
    fontSize: '14px',
    outline: 'none',
    background: 'white',
  },
  deleteProjectBtn: { background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px' },
  tasksList: { marginBottom: '10px', minHeight: '30px' },
  taskItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '7px 0',
    borderBottom: '1px solid rgba(99,102,241,0.08)',
    cursor: 'grab',
    userSelect: 'none',
  },
  taskLabel: { display: 'flex', alignItems: 'center', gap: '8px', flex: 1, cursor: 'pointer' },
  checkbox: { width: '16px', height: '16px', cursor: 'pointer' },
  taskTitle: { fontSize: '13px', color: '#1a1a2e', cursor: 'pointer' },
  editTaskInput: {
    flex: 1,
    padding: '2px 6px',
    borderRadius: '6px',
    border: '1px solid #2575fc',
    fontSize: '13px',
    outline: 'none',
    background: 'white',
  },
  deleteTaskBtn: { background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px' },
  addTask: { display: 'flex', gap: '4px', marginTop: '8px' },
  taskInput: {
    flex: 1,
    padding: '7px 12px',
    borderRadius: '10px',
    border: '1px solid rgba(99,102,241,0.2)',
    background: 'rgba(255,255,255,0.85)',
    fontSize: '12px',
    outline: 'none',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
  },
  addTaskButton: {
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 3px 8px rgba(99,102,241,0.3)',
    flexShrink: 0,
  },
};

export default TaskBoard;